/** The Announcements API from the app (1.7.6): the list, one announcement, the draft, Clean it up, the audience, the presses. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AnnounceLang,
  AnnouncementDetail,
  AnnouncementInput,
  AnnouncementPreview,
  AnnouncementSendResult,
  AnnouncementsResponse,
  AudienceInput,
  AudiencePerson,
  AudiencePreview,
  CleanupResult,
  DraftCost,
  DraftFields,
  DraftResult,
  ExtraInput,
  ExtraRecipient,
} from '@shared/announcements';
import { apiFetch } from '@/lib/api';

const post = <T,>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
export const announcementPath = (id: string) => `/api/announcements/a/${encodeURIComponent(id)}`;

export function useAnnouncements() {
  return useQuery({ queryKey: ['announcements'], queryFn: () => apiFetch<AnnouncementsResponse>('/api/announcements'), staleTime: 15_000, refetchOnWindowFocus: true });
}

/** Live while batches are still leaving (re-read every 15 seconds while the page is open). */
export function useAnnouncement(id: string) {
  return useQuery({ queryKey: ['announcement', id], queryFn: () => apiFetch<AnnouncementDetail>(announcementPath(id)), enabled: !!id, staleTime: 5_000, refetchInterval: 15_000, refetchOnWindowFocus: true });
}

export function useExtras(enabled: boolean) {
  return useQuery({ queryKey: ['announcement-extras'], queryFn: () => apiFetch<{ extras: ExtraRecipient[] }>('/api/announcements/extras'), enabled, staleTime: 10_000 });
}

export function useAudiencePeople(q: string) {
  const query = q.trim();
  return useQuery({ queryKey: ['announcement-people', query], queryFn: () => apiFetch<{ people: AudiencePerson[] }>(`/api/announcements/people?q=${encodeURIComponent(query)}`), enabled: query.length >= 2, staleTime: 5_000 });
}

/** Settings → System: what the last draft used. */
export function useDraftCost(enabled: boolean) {
  return useQuery({ queryKey: ['announcement-draft-cost'], queryFn: () => apiFetch<DraftCost>('/api/announcements/draft-cost'), enabled, staleTime: 30_000, retry: false });
}

/** The box's first text (English and Spanish). Sends nothing. */
export const requestDraft = (a: { eventId?: string | null; fields?: Partial<DraftFields> }) => post<DraftResult>('/api/announcements/draft', a);
/** Clean it up: the corrected text for the office to accept or not. */
export const requestCleanup = (text: string, lang: AnnounceLang) => post<CleanupResult>('/api/announcements/cleanup', { text, lang });
/** The count by group before the press. */
export const requestAudience = (audience: AudienceInput, spanish: boolean) => post<AudiencePreview>('/api/announcements/audience', { audience, spanish });
export const saveAnnouncement = (a: AnnouncementInput) => post<AnnouncementDetail>('/api/announcements/save', a);
export const previewAnnouncement = (a: AnnouncementInput) => post<AnnouncementPreview>('/api/announcements/preview', a);
export const sendAnnouncement = (a: AnnouncementInput) => post<AnnouncementSendResult>('/api/announcements/send', a);
export const saveExtra = (a: ExtraInput) => post<{ extra: ExtraRecipient }>('/api/announcements/extras', a);
/** Take back every message that has not left yet. */
export const cancelWaiting = (id: string) => post<{ announcement: AnnouncementDetail; cancelled: number; message: string }>(announcementPath(id), { what: 'cancel' });
/** After the address was corrected: send again to that one person. */
export const sendAgain = (id: string, key: string) => post<{ announcement: AnnouncementDetail; message: string }>(announcementPath(id), { what: 'again', key });

/** After any press: the list, the announcement and the Extra recipients list refresh. */
export function useAnnouncementRefresh() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['announcements'] });
    void qc.invalidateQueries({ queryKey: ['announcement-extras'] });
    if (id) void qc.invalidateQueries({ queryKey: ['announcement', id] });
  };
}
